import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Menu, Minus, Plus, X } from "lucide-react";

/* ==================================================================== */
/*  HOARDEXA — RATE CARD                                                 */
/*  Same identity as Home: Signal Yellow #FFC800 · Ink #101014          */
/*  Chalk #FAFAF7 · Steel #55555C · Anton / Archivo / IBM Plex Mono     */
/*  All plan features below are placeholders — edit the `plans` and     */
/*  `comparison` arrays freely.                                          */
/* ==================================================================== */

const INK = "#101014";
const YELLOW = "#FFC800";

function GlobalStyle() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Anton&family=Archivo:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
      .hx { font-family: 'Archivo', system-ui, sans-serif; }
      .hx-display { font-family: 'Anton', 'Archivo Black', sans-serif; letter-spacing: 0.01em; }
      .hx-mono { font-family: 'IBM Plex Mono', ui-monospace, monospace; }
      .hx ::selection { background: ${YELLOW}; color: ${INK}; }
    `}</style>
  );
}

/* ------------------------------------------------------------------ */
/*  Plan data — dummy content, edit freely                               */
/* ------------------------------------------------------------------ */

const plans = [
  {
    id: "standard",
    name: "STANDARD",
    tagline: "Single-city owners moving off spreadsheets.",
    monthly: 2999,
    yearly: 2399,
    cta: "START STANDARD",
    featured: false,
    features: [
      "Up to 100 hoardings",
      "1 city / market",
      "3 team members",
      "Inventory registry with photos",
      "Availability calendar",
      "Basic campaign booking",
      "Email support",
    ],
  },
  {
    id: "pro",
    name: "PRO",
    tagline: "Growing teams running campaigns and collections.",
    monthly: 5999,
    yearly: 4799,
    cta: "UPGRADE TO PRO",
    featured: false,
    features: [
      "Up to 500 hoardings",
      "5 cities / markets",
      "10 team members",
      "Everything in Standard",
      "Receivables & payment tracking",
      "Proposal builder with exports",
      "Smart alerts & action cards",
      "Priority email support",
    ],
  },
  {
    id: "premium",
    name: "PREMIUM",
    tagline: "Full intelligence for multi-city operators.",
    monthly: 9999,
    yearly: 7999,
    cta: "GO PREMIUM",
    featured: true,
    features: [
      "Unlimited hoardings",
      "Unlimited cities / markets",
      "25 team members",
      "Everything in Pro",
      "Analytics & city intelligence",
      "AI-assisted summaries & reports",
      "Profit leakage detection",
      "Client memory & history",
      "Phone + WhatsApp support",
    ],
  },
  {
    id: "enterprise",
    name: "ENTERPRISE",
    tagline: "Large networks with custom needs.",
    monthly: null,
    yearly: null,
    cta: "TALK TO SALES",
    featured: false,
    features: [
      "Everything in Premium",
      "Unlimited team members",
      "Custom roles & permissions",
      "Dedicated onboarding & migration",
      "Custom reports & integrations",
      "SLA-backed uptime",
      "Dedicated account manager",
    ],
  },
];

const comparison = [
  {
    group: "INVENTORY & AVAILABILITY",
    rows: [
      ["Hoardings", "100", "500", "Unlimited", "Unlimited"],
      ["Cities / markets", "1", "5", "Unlimited", "Unlimited"],
      ["Photo library & site IDs", true, true, true, true],
      ["Availability calendar", true, true, true, true],
    ],
  },
  {
    group: "CAMPAIGNS & FINANCE",
    rows: [
      ["Campaign booking & date windows", true, true, true, true],
      ["Receivables & payment tracking", false, true, true, true],
      ["Expense tracking", false, true, true, true],
      ["Profit leakage detection", false, false, true, true],
    ],
  },
  {
    group: "SALES & INTELLIGENCE",
    rows: [
      ["Proposal builder & exports", false, true, true, true],
      ["Analytics & city intelligence", false, false, true, true],
      ["AI summaries & reports", false, false, true, true],
      ["Client memory & history", false, false, true, true],
    ],
  },
  {
    group: "TEAM & SUPPORT",
    rows: [
      ["Team members", "3", "10", "25", "Unlimited"],
      ["Custom roles & permissions", false, false, false, true],
      ["Managed onboarding & migration", false, false, false, true],
      ["Support channel", "Email", "Priority email", "Phone + WhatsApp", "Dedicated manager"],
    ],
  },
];

const pricingFaqs = [
  {
    q: "Can I switch plans later?",
    a: "Yes. Upgrades apply immediately; downgrades apply at the next billing cycle. Your data never changes shape between plans.",
  },
  {
    q: "Is there a free trial?",
    a: "Every plan starts with a 14-day free trial — no card required. Everything you enter carries over if you subscribe.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "Export all inventory, campaign, and finance data any time, on any plan. After cancellation, data is retained 30 days, then deleted.",
  },
  {
    q: "Do you help migrate from spreadsheets?",
    a: "Pro and Premium include guided import templates. Enterprise includes fully managed migration by our onboarding team.",
  },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                              */
/* ------------------------------------------------------------------ */

const rise = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

function formatINR(value) {
  return `₹${value.toLocaleString("en-IN")}`;
}

function Cell({ value }) {
  if (value === true) return <Check size={16} strokeWidth={3.5} className="mx-auto text-[#15803D]" />;
  if (value === false) return <Minus size={16} strokeWidth={3} className="mx-auto text-[#101014]/20" />;
  return <span className="hx-mono text-[11px] font-bold tracking-wide text-[#101014] sm:text-[12px]">{value}</span>;
}

/* ------------------------------------------------------------------ */
/*  Navbar                                                               */
/* ------------------------------------------------------------------ */

function PricingNavbar() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b-2 border-[#101014] bg-[#FAFAF7]">
      <div className="mx-auto flex h-16 max-w-7xl items-stretch justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3">
          <span className="hx-display flex h-9 items-center bg-[#101014] px-2.5 text-lg text-[#FFC800]">HX</span>
          <span className="hx-display text-lg tracking-wide text-[#101014]">HOARDEXA</span>
        </Link>

        <nav className="hx-mono hidden items-stretch text-[11px] font-semibold uppercase tracking-[0.08em] lg:flex xl:text-[12px] xl:tracking-[0.1em]">
          <Link to="/" className="flex items-center gap-2 border-l-2 border-[#101014]/10 px-3.5 text-[#101014] transition-colors hover:bg-[#FFC800] xl:px-5">
            <ArrowLeft size={13} /> Home
          </Link>
          <Link to="/login" className="flex items-center border-l-2 border-[#101014]/10 px-3.5 text-[#101014] transition-colors hover:bg-[#FFC800] xl:px-5">
            Sign In
          </Link>
          <Link
            to="/#contact"
            className="hx-mono flex items-center gap-2 border-l-2 border-[#101014] bg-[#101014] px-4 text-[11px] font-bold tracking-[0.08em] text-[#FFC800] transition-colors hover:bg-[#FFC800] hover:text-[#101014] xl:px-6 xl:text-[12px] xl:tracking-[0.1em]"
          >
            Book Demo <ArrowRight size={14} />
          </Link>
        </nav>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="my-3 flex items-center border-2 border-[#101014] px-3 text-[#101014] lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="hx-mono divide-y divide-[#101014]/10 border-t-2 border-[#101014] bg-[#FAFAF7] text-[13px] font-semibold uppercase tracking-[0.1em] lg:hidden">
          <Link to="/" className="block px-5 py-3.5 text-[#101014]">Home</Link>
          <Link to="/login" className="block px-5 py-3.5 text-[#101014]">Sign In</Link>
          <Link to="/#contact" className="flex items-center justify-between bg-[#101014] px-5 py-4 font-bold text-[#FFC800]">
            Book Demo <ArrowRight size={15} />
          </Link>
        </div>
      )}
    </header>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero + billing toggle                                                */
/* ------------------------------------------------------------------ */

function RateCardHero({ yearly, setYearly }) {
  return (
    <section className="border-b-2 border-[#101014] bg-[#FFC800] pt-16">
      <div className="mx-auto max-w-7xl px-4 pb-12 pt-12 sm:px-6 sm:pt-16 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="hx-mono flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-bold tracking-[0.2em] text-[#101014]/60"
        >
          <span className="bg-[#101014] px-2 py-1 text-[#FFC800]">EFFECTIVE 2026</span>
          <span>14-DAY FREE TRIAL ON EVERY PLAN · NO CARD REQUIRED</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="hx-display mt-6 text-[16vw] uppercase leading-[0.88] text-[#101014] sm:text-8xl lg:text-[9rem]"
        >
          Rate card.
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.15 }}
          className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between"
        >
          <p className="max-w-md text-[15px] font-semibold leading-snug text-[#101014] sm:text-base">
            Four plans, priced by sites and cities — the way outdoor media is actually priced. Start small, upgrade
            when the skyline grows.
          </p>

          {/* Billing toggle */}
          <div className="hx-mono inline-flex border-2 border-[#101014] text-[12px] font-bold uppercase tracking-[0.12em]" role="group" aria-label="Billing period">
            <button
              type="button"
              onClick={() => setYearly(false)}
              aria-pressed={!yearly}
              className={`px-5 py-3 transition-colors ${!yearly ? "bg-[#101014] text-[#FFC800]" : "text-[#101014] hover:bg-[#101014]/10"}`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setYearly(true)}
              aria-pressed={yearly}
              className={`border-l-2 border-[#101014] px-5 py-3 transition-colors ${yearly ? "bg-[#101014] text-[#FFC800]" : "text-[#101014] hover:bg-[#101014]/10"}`}
            >
              Yearly <span className={yearly ? "text-[#15E07A]" : "text-[#15803D]"}>−20%</span>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Plan panels                                                          */
/* ------------------------------------------------------------------ */

function PlanPanels({ yearly }) {
  return (
    <section className="bg-[#FAFAF7] py-14 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-[2px] border-2 border-[#101014] bg-[#101014] sm:grid-cols-2 xl:grid-cols-4">
        {plans.map((plan, index) => {
          const price = plan.monthly === null ? null : yearly ? plan.yearly : plan.monthly;
          return (
            <motion.div
              key={plan.id}
              {...rise}
              transition={{ ...rise.transition, delay: index * 0.06 }}
              className={`relative flex flex-col p-6 sm:p-7 ${plan.featured ? "bg-[#101014] text-[#FAFAF7]" : "bg-[#FAFAF7] text-[#101014]"}`}
            >
              {plan.featured && (
                <span className="hx-mono absolute right-5 top-5 bg-[#FFC800] px-2.5 py-1 text-[9px] font-bold tracking-[0.18em] text-[#101014]">
                  ★ MOST BOOKED
                </span>
              )}

              <div className="hx-mono text-[11px] font-bold tracking-[0.22em]">{plan.name}</div>
              <p className={`mt-2 min-h-10 text-[13px] font-medium leading-snug ${plan.featured ? "text-white/60" : "text-[#55555C]"}`}>
                {plan.tagline}
              </p>

              <div className="mt-6 py-1">
                {price === null ? (
                  <div className="hx-display text-5xl leading-none">Custom</div>
                ) : (
                  <div className="hx-display text-5xl leading-none">
                    {formatINR(price)}
                    <span className="hx-mono ml-1 align-baseline text-[11px] font-bold tracking-widest opacity-50">/MO</span>
                  </div>
                )}
                <div className={`hx-mono mt-2.5 text-[10px] font-bold tracking-[0.14em] ${plan.featured ? "text-[#FFC800]" : "text-[#55555C]"}`}>
                  {price === null ? "TAILORED QUOTE FOR YOUR NETWORK" : yearly ? "BILLED YEARLY · SAVE 20%" : "BILLED MONTHLY"}
                </div>
              </div>

              <Link
                to={plan.id === "enterprise" ? "/#contact" : "/login"}
                className={`hx-mono mt-6 flex items-center justify-center gap-2.5 px-4 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors ${
                  plan.featured
                    ? "bg-[#FFC800] text-[#101014] hover:bg-[#FAFAF7]"
                    : "bg-[#101014] text-[#FFC800] hover:bg-[#FFC800] hover:text-[#101014]"
                }`}
              >
                {plan.cta} <ArrowRight size={14} />
              </Link>

              <ul className={`mt-7 flex-1 space-y-3 border-t pt-6 ${plan.featured ? "border-white/15" : "border-[#101014]/15"}`}>
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-[13px] font-semibold leading-snug">
                    <Check size={14} strokeWidth={3.5} className={`mt-0.5 shrink-0 ${plan.featured ? "text-[#FFC800]" : "text-[#15803D]"}`} />
                    <span className={plan.featured ? "text-white/85" : "text-[#101014]"}>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Comparison table                                                     */
/* ------------------------------------------------------------------ */

function Comparison() {
  return (
    <section className="bg-[#FAFAF7] pb-14 sm:pb-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...rise} className="hx-mono flex items-center gap-3 text-[11px] font-bold tracking-[0.18em] text-[#101014]">
          <span className="bg-[#101014] px-2 py-1 text-[#FFC800]">SPEC</span>
          <span className="text-[#55555C]">EVERY CAPABILITY, SIDE BY SIDE</span>
          <span className="h-px flex-1 bg-[#101014]/15" />
        </motion.div>

        <motion.div {...rise} className="mt-8 overflow-x-auto border-2 border-[#101014]">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead>
              <tr className="border-b-2 border-[#101014] bg-[#FAFAF7]">
                <th className="hx-mono px-5 py-4 text-[10px] font-bold tracking-[0.2em] text-[#55555C]">CAPABILITY</th>
                {plans.map((plan) => (
                  <th key={plan.id} className={`px-4 py-4 text-center ${plan.featured ? "bg-[#FFC800]" : ""}`}>
                    <div className="hx-display text-base uppercase text-[#101014]">{plan.name}</div>
                    <div className="hx-mono mt-0.5 text-[9px] font-bold tracking-[0.14em] text-[#101014]/50">
                      {plan.monthly === null ? "CUSTOM" : `${formatINR(plan.monthly)}/MO`}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparison.map((group) => (
                <Fragment key={group.group}>
                  <tr className="bg-[#101014]">
                    <td colSpan={5} className="hx-mono px-5 py-2.5 text-[10px] font-bold tracking-[0.22em] text-[#FFC800]">
                      {group.group}
                    </td>
                  </tr>
                  {group.rows.map(([label, ...cells]) => (
                    <tr key={label} className="border-b border-[#101014]/15 last:border-b-0 hover:bg-[#FFC800]/15">
                      <td className="px-5 py-3.5 text-[13px] font-semibold text-[#101014]">{label}</td>
                      {cells.map((cell, cellIndex) => (
                        <td key={cellIndex} className={`px-4 py-3.5 text-center ${plans[cellIndex].featured ? "bg-[#FFC800]/25" : ""}`}>
                          <Cell value={cell} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </motion.div>

        <motion.div {...rise} className="hx-mono mt-6 grid gap-px border-2 border-[#101014] bg-[#101014] text-[11px] font-bold tracking-[0.12em] sm:grid-cols-3">
          {[
            "14-DAY FREE TRIAL — FULL ACCESS, NO CARD",
            "EXPORT ALL YOUR DATA, ANY TIME, ANY PLAN",
            "GUIDED ONBOARDING — LIVE IN DAYS, NOT WEEKS",
          ].map((item) => (
            <div key={item} className="flex items-center gap-3 bg-[#FAFAF7] px-4 py-3.5 text-[#101014]">
              <Check size={14} strokeWidth={3.5} className="shrink-0 text-[#15803D]" />
              {item}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ + CTA                                                            */
/* ------------------------------------------------------------------ */

function PricingFAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section className="border-t-2 border-[#101014] bg-[#FAFAF7] py-16 sm:py-20">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
        <motion.h2 {...rise} className="hx-display text-4xl uppercase leading-[0.95] text-[#101014] sm:text-5xl">
          Billing,
          <br />
          answered.
        </motion.h2>

        <motion.div {...rise} className="border-t-2 border-[#101014]">
          {pricingFaqs.map((item, index) => {
            const isOpen = open === index;
            return (
              <div key={item.q} className="border-b-2 border-[#101014]">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  className={`flex w-full items-center justify-between gap-4 px-1 py-5 text-left transition-colors sm:px-3 ${isOpen ? "" : "hover:bg-[#FFC800]/25"}`}
                >
                  <span className="hx-display text-lg uppercase leading-tight text-[#101014] sm:text-xl">{item.q}</span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center border-2 border-[#101014] text-[#101014]">
                    {isOpen ? <Minus size={15} strokeWidth={3} /> : <Plus size={15} strokeWidth={3} />}
                  </span>
                </button>
                {isOpen && (
                  <p className="px-1 pb-6 text-[15px] font-medium leading-relaxed text-[#55555C] sm:px-3">{item.a}</p>
                )}
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

function RateCardCTA() {
  return (
    <section className="bg-[#101014] py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...rise} className="border-4 border-[#3A3A40] bg-[#FFC800] shadow-[10px_10px_0_rgba(0,0,0,0.45)]">
          <div className="flex flex-col items-start justify-between gap-6 p-8 sm:p-10 lg:flex-row lg:items-center">
            <div>
              <h2 className="hx-display text-3xl uppercase leading-[0.95] text-[#101014] sm:text-5xl">
                Not sure which
                <br />
                plan fits your network?
              </h2>
              <p className="mt-4 max-w-md text-[15px] font-semibold leading-snug text-[#101014]/80">
                Tell us your cities and inventory size — we recommend a starting point and build the demo around your
                operation.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              <Link
                to="/#contact"
                className="hx-mono group inline-flex items-center gap-3 bg-[#101014] px-7 py-4 text-[12px] font-bold uppercase tracking-[0.12em] text-[#FFC800] shadow-[5px_5px_0_rgba(16,16,20,0.3)] transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[3px_3px_0_rgba(16,16,20,0.3)]"
              >
                Book a Demo <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="mailto:sales@hoardexa.com"
                className="hx-mono inline-flex items-center gap-3 border-2 border-[#101014] px-7 py-4 text-[12px] font-bold uppercase tracking-[0.12em] text-[#101014] transition-colors hover:bg-[#101014] hover:text-[#FFC800]"
              >
                sales@hoardexa.com
              </a>
              <Link
                to="/login"
                className="hx-mono inline-flex items-center gap-3 border-2 border-[#101014] px-7 py-4 text-[12px] font-bold uppercase tracking-[0.12em] text-[#101014] transition-colors hover:bg-[#101014] hover:text-[#FFC800]"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
          <div className="hx-mono flex items-center justify-between border-t-2 border-[#101014]/20 px-5 py-2 text-[10px] font-bold tracking-[0.2em] text-[#101014]/50">
            <span>HX-CTA</span>
            <span className="hidden sm:block">THIS SPACE CONVERTED YOU. IMAGINE WHAT YOURS COULD DO.</span>
            <span>40 × 10 FT</span>
          </div>
        </motion.div>

        <div className="hx-mono mt-10 flex flex-col justify-between gap-2 border-t border-white/15 pt-5 text-[10px] font-bold tracking-[0.16em] text-white/40 sm:flex-row">
          <span>© {new Date().getFullYear()} HOARDEXA — OUTDOOR MEDIA OPERATING SYSTEM</span>
          <div className="flex gap-5">
            <Link to="/" className="uppercase transition-colors hover:text-[#FFC800]">Home</Link>
            <Link to="/login" className="uppercase transition-colors hover:text-[#FFC800]">Sign In</Link>
            <Link to="/privacy-policy" className="uppercase transition-colors hover:text-[#FFC800]">Privacy</Link>
            <Link to="/careers" className="uppercase transition-colors hover:text-[#FFC800]">Careers</Link>
            <a href="mailto:sales@hoardexa.com" className="uppercase transition-colors hover:text-[#FFC800]">Sales</a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                 */
/* ------------------------------------------------------------------ */

export function Pricing() {
  const [yearly, setYearly] = useState(true);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hx min-h-screen bg-[#FAFAF7] text-[#101014] antialiased">
      <GlobalStyle />
      <PricingNavbar />
      <main>
        <RateCardHero yearly={yearly} setYearly={setYearly} />
        <PlanPanels yearly={yearly} />
        <Comparison />
        <PricingFAQ />
        <RateCardCTA />
      </main>
    </motion.div>
  );
}

export default Pricing;
